import type { ClintplayEvent } from '../types';

type EventCallback = (event: ClintplayEvent) => void;

export class WebSocketTracker {
  private OriginalWebSocket: typeof WebSocket | null = null;
  private active = false;

  constructor(
    private sessionId: string,
    private emit: EventCallback
  ) {}

  start(): void {
    this.active = true;
    this.patchWebSocket();
  }

  stop(): void {
    this.active = false;
    this.restoreWebSocket();
  }

  private patchWebSocket(): void {
    this.OriginalWebSocket = globalThis.WebSocket;
    const self = this;
    const Original = this.OriginalWebSocket;

    const PatchedWebSocket = function (
      this: WebSocket,
      url: string | URL,
      protocols?: string | string[]
    ) {
      const ws: WebSocket = protocols !== undefined
        ? new Original(url, protocols)
        : new Original(url);

      const urlStr = typeof url === 'string' ? url : url.toString();

      ws.addEventListener('error', () => {
        if (!self.active) return;
        self.emit({
          type: 'error',
          timestamp: Date.now(),
          sessionId: self.sessionId,
          data: {
            message: `WebSocket error: ${urlStr}`,
            type: 'onerror',
            source: 'websocket-tracker',
          },
        });
      });

      ws.addEventListener('close', (event: CloseEvent) => {
        if (!self.active) return;
        if (event.code >= 1002) {
          self.emit({
            type: 'error',
            timestamp: Date.now(),
            sessionId: self.sessionId,
            data: {
              message: `WebSocket closed abnormally (${event.code}): ${urlStr}`,
              type: 'onerror',
              source: 'websocket-tracker',
            },
          });
        }
      });

      return ws;
    } as unknown as typeof WebSocket;

    // Preserve static properties and prototype
    PatchedWebSocket.prototype = Original.prototype;
    Object.defineProperty(PatchedWebSocket, 'CONNECTING', { value: Original.CONNECTING });
    Object.defineProperty(PatchedWebSocket, 'OPEN', { value: Original.OPEN });
    Object.defineProperty(PatchedWebSocket, 'CLOSING', { value: Original.CLOSING });
    Object.defineProperty(PatchedWebSocket, 'CLOSED', { value: Original.CLOSED });

    globalThis.WebSocket = PatchedWebSocket;
  }

  private restoreWebSocket(): void {
    if (this.OriginalWebSocket) {
      globalThis.WebSocket = this.OriginalWebSocket;
      this.OriginalWebSocket = null;
    }
  }
}
